/**
 * Profile-driven pay shape mappers.
 *
 * These translate a worker's CURRENT profile (People service details) into the engagement worker
 * pay fields. They are the single source of truth shared by the add flow (handleWorkerSelection) and
 * the restore flow (restoreWorker) in WorkersSection, so the two cannot drift — a worker converted to
 * T4 Salaried while removed is re-added/restored as salaried, never with a stale hourly pay block
 * (GW-210). Each mapper fully clears the opposite pay block so switching directions is clean.
 */
import { WORKER_TYPE, type WorkerFormData } from '@/lib/schemas'
import { RATE_UNIT } from '@/lib/constants/work-order-codes'
import { type T4SalariedDetails, type EngagementDetails, type ProfilePayRate } from '@/types/api/people'
import { type WorkerPayRate } from '@/types/api/engagement'
import { allocateRateSlots, heldProfileRates, isBaseRateType } from '@/lib/utils/worker-rate-slots'

// Null-safe like its hourly sibling: if the People response omits the T4SalariedDetails block entirely,
// this still returns a salaried shape with a null reportingPayRate (and the hourly block cleared) rather
// than throwing — so the form schema blocks the save instead of restore silently keeping the stale shape.
export const salariedPayShapeFromDetails = (d: T4SalariedDetails | null | undefined): Partial<WorkerFormData> => ({
  type: WORKER_TYPE.T4_SALARIED,
  reportingPayRate: d?.ReportingPayRate ?? null,
  // Treat 0 as invalid (no scheduled rate). scheduledPayRateEffectiveDate is the canonical date field.
  scheduledReportingPayRate: d?.ScheduledReportingPayRate && d.ScheduledReportingPayRate > 0 ? d.ScheduledReportingPayRate : null,
  scheduledPayRateEffectiveDate: d?.ScheduledSalaryEffectiveDate ?? null,
  budgetedHours: d?.HoursPerDay ?? null,
  // clear the hourly block
  payRates: [],
  payRate: null,
  payRateUnit: RATE_UNIT.HOUR,
  paySchedule: '',
  scheduledPayRate: null,
  scheduledReportingPayRateEffectiveDate: null,
})

/**
 * What the engagement's tasks need a worker to be paid at: a billable rate where an active task is
 * billed, a non-billable one where an active task is not. An engagement with no tasks needs neither.
 */
export interface TaskCoverage {
  needsBillable: boolean
  needsNonBillable: boolean
}

/**
 * The rates to start a worker on: their Primary rate first, as the base rate every placement holds, then
 * the first rate of any billability the tasks need that Primary does not cover.
 *
 * Hours land on a rate whose billability matches their task's, so this is the smallest selection the
 * engagement will accept. A caller whose engagement bills nobody for hours passes no coverage, because
 * every rate there is non-billable and Primary alone covers every task.
 *
 * A profile with no Primary rate starts the worker on the first rate of each billability the tasks need,
 * so the dialog still shows what they are paid, and the save says to add Primary to the profile.
 */
export const ratesCoveringTasks = (
  rates: readonly ProfilePayRate[],
  coverage: TaskCoverage | undefined,
): ProfilePayRate[] => {
  const primary = rates.find(rate => isBaseRateType(rate.RateTypeCode))
  const primaryCoversBillable = !!primary && primary.Billable !== false
  const primaryCoversNonBillable = !!primary && primary.Billable === false

  const firstBillable = coverage?.needsBillable && !primaryCoversBillable
    ? rates.find(rate => rate !== primary && rate.Billable !== false)
    : undefined
  const firstNonBillable = coverage?.needsNonBillable && !primaryCoversNonBillable
    ? rates.find(rate => rate !== primary && rate.Billable === false)
    : undefined

  // Profile order after Primary, so the rates read the way the profile lists them.
  const seeded = [
    ...(primary ? [primary] : []),
    ...rates.filter(rate => rate === firstBillable || rate === firstNonBillable),
  ]

  return seeded.length > 0 ? seeded : rates.slice(0, 1)
}

/**
 * The hourly pay shape for a worker, from their profile as it is now.
 *
 * `held` is the selection a worker already had on this engagement, passed when bringing one back rather
 * than adding one. Those rates return at the profile's current amounts, less any the profile no longer
 * offers. With nothing held, or nothing held still offered, the worker starts on the smallest selection
 * the tasks accept and gains their other rates by being selected onto them.
 */
export const hourlyPayShapeFromDetails = (
  d: EngagementDetails | null | undefined,
  coverage?: TaskCoverage,
  held?: readonly WorkerPayRate[] | null,
): Partial<WorkerFormData> => {
  const profileRates = profileRatesFor(d)
  const stillHeld = heldProfileRates(held, profileRates)
  const payRates = allocateRateSlots(stillHeld.length > 0 ? stillHeld : ratesCoveringTasks(profileRates, coverage))

  // The flat pay fields describe the base rate, which is the first of the rates seeded rather than the
  // profile's own first rate: Primary need not be first on the profile, and the two must agree or the
  // dialog shows one amount while the list shows another.
  const baseRate = payRates[0]

  return {
    type: WORKER_TYPE.REGULAR,
    payRates,
    payRate: baseRate?.payRate ?? d?.PayRate ?? null,
    payRateUnit: baseRate?.payRateUnitCode ?? d?.RateUnitCode ?? RATE_UNIT.HOUR,
    paySchedule: d?.PaymentScheduleCode ?? '',
    scheduledPayRate: baseRate?.scheduledPayRate ?? d?.ScheduledPayRate ?? null,
    scheduledPayRateEffectiveDate:
      baseRate?.scheduledPayRateEffectiveDate ?? d?.ScheduledPayRateEffectiveDate ?? null,
    budgetedHours: d?.HoursPerDay ?? null,
    // clear the salaried block
    reportingPayRate: null,
    scheduledReportingPayRate: null,
    scheduledReportingPayRateEffectiveDate: null,
  }
}

/**
 * The rates a profile offers, with the flat fields standing in for a profile that has not been saved since
 * the list existed. There is no backfill, so this lifting is what an unmigrated profile relies on.
 */
export const profileRatesFor = (d: EngagementDetails | null | undefined): ProfilePayRate[] => {
  if (d?.PayRates?.length) {
    return d.PayRates
  }

  if (d?.PayRate == null && !d?.RateUnitCode) {
    return []
  }

  return [{
    PayRate: d?.PayRate,
    RateUnitCode: d?.RateUnitCode,
    ScheduledPayRate: d?.ScheduledPayRate,
    ScheduledPayRateEffectiveDate: d?.ScheduledPayRateEffectiveDate,
  }]
}
